import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { AVISOS, LINKS } from '../constants/constants';

const getLabel = (options, value) =>
  options?.find((o) => o.value === value)?.label || '-';

const sanitizeText = (str) => {
  if (str == null) return '';
  return String(str)
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')      
    .replace(/[^\x20-\x7E\n]/g, '');      
};

const BRAND = { r: 41, g: 128, b: 185 };

const CATEGORIAS = {
  'design': ['iabstd', 'iabrm', 'dvast'],
  'html5 production': ['expandivel', 'fullscreen', 'shoppable'],
  'setup & testing': ['setup', 'integration'],
};

const categorizarFormato = (formato) => {
  const key = String(formato || '').toLowerCase();
  for (const categoria in CATEGORIAS) {
    if (CATEGORIAS[categoria].includes(key)) return categoria;
  }
  return 'others';
};

/**
 * Coleta mensagens de aviso UNICAS para a LINHA do criativo.
 */
const getRowAlerts = (res, est) => {
  const msgs = new Set();
  Object.keys(AVISOS || {}).forEach((k) => {
    if (res?.[k]) {
      const v = AVISOS[k];
      if (v) msgs.add(sanitizeText(String(v).replace(/[–•]/g, '-').trim()));
    }
  });

  const f = String(res?.formato || '').toLowerCase();
  if (['iabstd', 'iabrm'].includes(f)) {
    if (AVISOS?.avisoTag) msgs.add(sanitizeText(String(AVISOS.avisoTag).trim()));
  } else if (f === 'dvast') {
    const msg = est?.dvastSkip ? AVISOS?.avisoVast3 : AVISOS?.avisoVast2;
    if (msg) msgs.add(sanitizeText(String(msg).trim()));
  }

  return [...msgs].filter(Boolean);
};


const getCellPadding = (cell) => {
  if (typeof cell?.padding === 'function') {
    const p = cell.padding();
    return { top: p.top, right: p.right, bottom: p.bottom, left: p.left };
  }
  const cp = cell?.styles?.cellPadding ?? 0;
  if (typeof cp === 'number') return { top: cp, right: cp, bottom: cp, left: cp };
  return {
    top: cp?.top ?? 0,
    right: cp?.right ?? 0,
    bottom: cp?.bottom ?? 0,
    left: cp?.left ?? 0,
  };
};


export const gerarPDF = ({
  estimativas,
  resultado,
  formatoOptions,
  stepsOptions,
  alimentacaoOptions,
  urgenciaOptions,
  mockupOptions,
  volumetriaOptions,
  getAdaptacaoLabel,
  showAlert,
  cabecalhoImg,
  rodapeImg,
  moeda = 'USD',
  nomeProjeto,
  nomeCliente,
  responsavel,
  links // Recebido do AdminConfig
}) => {
  try {
    if (!Array.isArray(estimativas) || !Array.isArray(resultado) || !estimativas.length || !resultado.length) {
      showAlert?.('excluir', 'Erro: Estimativas ou resultados estão vazios.');
      return;
    }

    // Fallback para LINKS padrão se não houver config
    const LINK_DCO = "https://www.adsmovilanalytics.com.br/article/dynamic-creative-guidelines";

    const doc = new jsPDF('p', 'mm', 'a4');
    const pageW = doc.internal.pageSize.getWidth();
    const marginL = 15;
    let y = 18;

    // Header logo
    if (cabecalhoImg) doc.addImage(cabecalhoImg, 'PNG', pageW - 55, y - 3, 40, 10);

    // Info cliente
    doc.setFontSize(10).setFont(undefined, 'normal').setTextColor(45, 45, 45);
    doc.text(sanitizeText(`Submitted by: ${responsavel || '-'}`), marginL, y); y += 6.5;
    doc.text(sanitizeText(`${nomeCliente || '-'}`), marginL, y); y += 6.5;
    doc.text(sanitizeText(`Date of Proposal: ${new Date().toLocaleDateString('en-GB')}`), marginL, y); y += 10;

    // Linha divisória
    doc.setDrawColor(225, 225, 225).setLineWidth(0.25);
    doc.line(marginL, y, pageW - marginL, y); y += 10;

    // Nome do projeto
    doc.setFontSize(16).setFont(undefined, 'bold').setTextColor(0, 0, 0);
    doc.text(sanitizeText(nomeProjeto || 'Project'), marginL, y); y += 12;

    // Intro
    doc.setFontSize(10).setFont(undefined, 'normal').setTextColor(45, 45, 45);
    doc.text(sanitizeText('Thank you for considering us for your project. Below is a clear breakdown of the proposed services and estimated costs.'), marginL, y);
    y += 12;

    // Reset color
    doc.setTextColor(0, 0, 0);

    const groups = {};
    const linksByCategory = {};

    resultado.forEach((res) => {
      const est = estimativas.find((e) => e.id === res.id);
      if (!est) return;

      const category = categorizarFormato(res.formato);
      let labelFormato = formatoOptions?.find(o => o.value === res.formato)?.label || res.formato || '-';
      if (res.formato === 'shoppable' && est.carrossel) labelFormato += ' with carousel';
      if (res.formato === 'dvast' && est.dvastSkip) labelFormato += ' with skip';

      const rowAlerts = getRowAlerts(res, est);
      const warningLine = rowAlerts.length ? `[[WARN]] ${rowAlerts.join(' • ')}` : '';

      const descParts = [
        warningLine,
        `Format Type: ${labelFormato}`,
        `Steps: ${getLabel(stepsOptions, est.steps)}`,
        `Versions: ${est.versoes || '-'}`,
        `Feed Method: ${getLabel(alimentacaoOptions, est.alimentacao)}`,
        `Urgency: ${getLabel(urgenciaOptions, est.urgencia)}`,
        `Mockup Provided: ${getLabel(mockupOptions, est.mockup)}`,
        `${getAdaptacaoLabel?.(est.mockup) || 'Adaptations'}: ${est.desdobramentos || '-'}`,
        `Dimensions: ${[...(est.dimensoesSelecionadas || []), est.outrasDimensoes].filter(Boolean).join(', ') || '-'}`,
        `Volumetry: ${getLabel(volumetriaOptions, est.volumetria)}`,
        ...(est.viewabilityAvancado ? ['Viewability Features: Yes'] : []),
        ...(est.possuiItensDinamicos ? [
          'Dynamic Items: Yes',
          `Dynamic Item Description: ${est.descricaoItensDinamicos || '-'}`
        ] : []),
      ].filter(Boolean);

      const description = sanitizeText(descParts.join('\n'));

      const precoBase = Number(res.precoBase) || 0;
      const acrescimoValor = Number(res.acrescimoValor) || 0;
      const acrescimoPerc = Number(res.acrescimoUrgencia) || 0;
      const custoPowerfeed = Number(res.custoPowerfeed) || 0;
      const precoPorLinhaPowerfeeds = Number(res.precoPorLinhaPowerfeeds) || 0;
      const versoes = Number(est.versoes) || Number(res.versoes) || 0;
      const total = Number(res.preco) || 0;

      const financial = [
        acrescimoValor > 0
          ? `Priority: ${acrescimoPerc}% of ${precoBase.toFixed(2)} = ${moeda} ${acrescimoValor.toFixed(2)}`
          : null,
        custoPowerfeed > 0
          ? `PowerFeeds: ${versoes} x ${moeda} ${precoPorLinhaPowerfeeds.toFixed(3)} = ${moeda} ${custoPowerfeed.toFixed(2)}`
          : null,
        `Total: ${moeda} ${total.toFixed(2)}`
      ].filter(Boolean).join('\n');

      if (!groups[category]) groups[category] = [];
      groups[category].push([
        sanitizeText(`Creative #${res.id} - ${labelFormato}`),
        description,
        sanitizeText(financial)
      ]);
    });

    // Render tabelas
    for (const category in groups) {
      doc.setFontSize(11).setFont(undefined, 'bold');
      doc.text(sanitizeText(category.replace(/\b\w/g, c => c.toUpperCase())), marginL, y);
      y += 6;
      doc.setFont(undefined, 'normal');

      autoTable(doc, {
        head: [['Item', 'Description', `Amount (${moeda})`]],
        body: groups[category],
        startY: y,
        margin: { left: marginL, right: marginL },
        styles: { fontSize: 9, cellPadding: 3, lineWidth: 0.1, valign: 'top',overflow: 'linebreak' },
        headStyles: { fillColor: [BRAND.r, BRAND.g, BRAND.b], halign: 'left', textColor: 255 },
        bodyStyles: { halign: 'left' },
        columnStyles: { 0: { cellWidth: 50 }, 1: { cellWidth: 90 }, 2: { cellWidth: 40, halign: 'right', overflow: 'linebreak' } },
      });

      y = (doc.lastAutoTable?.finalY || y) + 10;
    }

    // Totais gerais
    const totalHours = resultado.reduce((acc, r) => acc + (Number(r?.horas) || 0), 0);
    const totalPrice = resultado.reduce((acc, r) => acc + (Number(r?.preco) || 0), 0);

    y += 14;
    doc.setFont(undefined, 'bold').setFontSize(10);
    doc.text(sanitizeText(`TOTAL PROJECT COST: ${moeda} ${totalPrice.toFixed(2)}`), pageW - marginL, y, { align: 'right' });
    y += 6;
    doc.setFont(undefined, 'normal').setFontSize(9).setTextColor(85, 85, 85);
    doc.text(sanitizeText(`Total Effort: ${totalHours.toFixed(2)} h`), pageW - marginL, y, { align: 'right' });

    // Links globais
    y += 10;
    doc.setFontSize(9).setTextColor(BRAND.r, BRAND.g, BRAND.b);
    //if (linksConfig.psdMockup) { doc.text('•', marginL, y); doc.textWithLink('Download PSD Mockup', marginL + 5, y, { url: linksConfig.psdMockup }); y += 5; }
    //if (linksConfig.entregaMockup) { doc.text('•', marginL, y); doc.textWithLink('Delivery Instructions', marginL + 5, y, { url: linksConfig.entregaMockup }); y += 6; }
    doc.text('Dynamic Creative Guidelines', marginL, y);
    doc.textWithLink(LINK_DCO, marginL, y + 4, { url: LINK_DCO });
    y += 10;
    doc.setTextColor(0, 0, 0);

    // Termos
    y += 6;
    doc.setFontSize(10).setFont(undefined, 'normal').setTextColor(45, 45, 45);
    doc.text(sanitizeText('This proposal is valid for 30 days from the date of issue.'), marginL, y); y += 7;
    doc.text(sanitizeText('Please contact us for any adjustments or questions regarding this estimate.'), marginL, y); y += 8;

    if (rodapeImg) doc.addImage(rodapeImg, 'PNG', marginL, 280, 30, 10);

    // Save PDF
    //const safeClient = (nomeCliente || '').replace(/\s+/g, '_');
    //const safeProject = (nomeProjeto || 'DCO_Estimate').replace(/\s+/g, '_');
    //doc.save(`${safeClient}_${safeProject}_Proposal.pdf`);
    //showAlert?.('adicionar', 'Success! PDF generated.');

    const pdfBlob = doc.output('blob');
    return pdfBlob;
  } catch (err) {
    console.error(err);
    showAlert?.('excluir', `Generation error: ${err?.message || err}`);
  }
};
